import React, { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Html5Qrcode } from "html5-qrcode";
import { toast } from "sonner";
import { Camera, CameraOff, CheckCircle2, AlertTriangle, XCircle, ScanLine } from "lucide-react";

export default function Scanner() {
  const [manual, setManual] = useState("");
  const [result, setResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);

  const doScan = async (token) => {
    if (!token) return;
    try {
      const { data } = await api.post("/tickets/scan", { qr_token: token });
      setResult(data);
      if (data.result === "ok") toast.success("Einlass bestätigt");
      else if (data.result === "already") toast.warning("Bereits eingecheckt");
      else toast.error("Ungültig");
    } catch (e) {
      setResult({ result: "invalid", ticket: null, error: formatApiErrorDetail(e.response?.data?.detail) });
      toast.error("Ticket ungültig");
    }
  };

  const startCam = async () => {
    setScanning(true);
    try {
      const q = new Html5Qrcode("qr-region");
      scannerRef.current = q;
      await q.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: 260 },
        (decoded) => {
          // parse if it's a URL
          let token = decoded;
          try {
            const u = new URL(decoded);
            const parts = u.pathname.split("/").filter(Boolean);
            token = parts[parts.length - 1] || decoded;
          } catch { /* not a URL */ }
          q.pause(true);
          doScan(token).finally(() => setTimeout(() => q.resume(), 1600));
        },
        () => {}
      );
    } catch (e) {
      toast.error("Kamera nicht verfügbar. Bitte manuell eingeben.");
      setScanning(false);
    }
  };
  const stopCam = async () => {
    try { await scannerRef.current?.stop(); await scannerRef.current?.clear(); } catch { /* ignore */ }
    setScanning(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => { stopCam(); }, []);

  const bg = result?.result === "ok" ? "bg-[#0F2F14] border-[#2E7D32]" :
             result?.result === "already" ? "bg-[#3B2C0A] border-[#D4AF37]" :
             result ? "bg-[#3B0C10] border-[#7A1921]" : "card-lux";

  return (
    <div className="space-y-6 max-w-2xl" data-testid="scanner-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A398]">Einlass</div>
        <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl">QR-Scanner</h1>
      </div>

      <div className="card-lux p-4">
        <div id="qr-region" className="w-full aspect-square max-w-xs mx-auto bg-black rounded overflow-hidden" />
        <div className="flex gap-2 mt-3 justify-center">
          {!scanning ? (
            <button data-testid="scanner-start-btn" onClick={startCam} className="bg-[#D4AF37] text-black px-4 py-2 rounded text-sm uppercase tracking-widest flex items-center gap-2"><Camera size={14} /> Kamera starten</button>
          ) : (
            <button data-testid="scanner-stop-btn" onClick={stopCam} className="border border-[#2A2A2A] px-4 py-2 rounded text-sm uppercase tracking-widest flex items-center gap-2 text-[#A8A398]"><CameraOff size={14} /> Stopp</button>
          )}
        </div>
      </div>

      <div className="card-lux p-4">
        <div className="text-xs uppercase tracking-widest text-[#A8A398] mb-2">Manuell eingeben</div>
        <div className="flex gap-2">
          <input value={manual} onChange={(e) => setManual(e.target.value)} placeholder="Ticket-Nr oder Token"
            className="flex-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37]" />
          <button data-testid="scanner-manual-btn" onClick={() => doScan(manual)} className="bg-[#D4AF37] text-black px-4 py-2 rounded text-sm uppercase tracking-widest flex items-center gap-2"><ScanLine size={14} /> Prüfen</button>
        </div>
      </div>

      {result && (
        <div className={`border rounded-xl p-6 ${bg}`} data-testid="scanner-result">
          {result.result === "ok" && (
            <div>
              <div className="flex items-center gap-2 text-[#9BE0A5] uppercase tracking-widest text-sm"><CheckCircle2 /> Einlass bestätigt</div>
              <Info t={result.ticket} />
            </div>
          )}
          {result.result === "already" && (
            <div>
              <div className="flex items-center gap-2 text-[#EEDC82] uppercase tracking-widest text-sm"><AlertTriangle /> Bereits eingecheckt</div>
              <Info t={result.ticket} />
              <div className="text-xs mt-2 text-[#A8A398]">Zuerst gescannt: {result.ticket?.scanned_at?.slice(0, 16).replace("T", " ")}</div>
            </div>
          )}
          {result.result === "invalid" && (
            <div>
              <div className="flex items-center gap-2 text-[#EE9CA1] uppercase tracking-widest text-sm"><XCircle /> Ungültiges Ticket</div>
              {result.error && <div className="text-sm text-[#A8A398] mt-2">{result.error}</div>}
            </div>
          )}
          {result.result === "storniert" && (
            <div className="text-[#EE9CA1] uppercase tracking-widest text-sm">Storniert</div>
          )}
        </div>
      )}
    </div>
  );
}
function Info({ t }) {
  if (!t) return null;
  return (
    <div className="mt-4 space-y-1">
      <div className="font-heading text-2xl">{t.movie_title}</div>
      <div className="text-sm text-[#A8A398]">{t.date} · {t.start_time} · {t.hall}</div>
      <div className="mt-2 text-lg font-mono">Reihe {t.row} · Platz {t.seat}</div>
      <div className="text-sm">{t.guest_name}</div>
      <div className="text-xs text-[#A8A398] font-mono">{t.ticket_number}</div>
    </div>
  );
}
